import { api } from '@/lib/api/client';
import { getDailySummary, getInventory, getPipelineStatus, getTodayQueue, getWaitTimes } from '@/api';
import type { QueueItem } from '@/features/queue/types';
import type { AppointmentSummary, DailySummary, InsightWindow, InventoryItem, PipelineStatus, SummaryWindow, WaitInsights, WaitTimes } from './types';

/** GET /api/appointment-slots/summary?date= — see the route for how each status is derived. */
export function fetchAppointmentSummary(date: string): Promise<AppointmentSummary> {
  return api.get('/appointment-slots/summary', { query: { date } });
}

/** GET /api/reports/wait-insights?window= — one row per OPD visit with where the time went. */
export function fetchWaitInsights(window: InsightWindow): Promise<WaitInsights> {
  return api.get('/reports/wait-insights', { query: { window } });
}

// Legacy src/api.ts calls, typed here at the boundary.
export const fetchDailySummary = (): Promise<DailySummary> => getDailySummary('today');
export const fetchTodayQueue = (): Promise<QueueItem[]> => getTodayQueue();
export const fetchInventory = (): Promise<InventoryItem[]> => getInventory();
export const fetchWaitTimes = (): Promise<WaitTimes> => getWaitTimes();
export const fetchPipelineStatus = (window: SummaryWindow): Promise<PipelineStatus> => getPipelineStatus(window);

/** The app's own liveness probe (server.js, outside /api). */
export type HealthState = 'ok' | 'down' | 'unknown';

export async function fetchHealth(): Promise<HealthState> {
  let res: Response;
  try { res = await fetch('/health', { cache: 'no-store' }); } catch { return 'down'; }
  if (!res.ok) return 'down';
  // In the Vite dev server /health is not proxied and comes back as the SPA's
  // index.html — that is "unknown", not "up".
  const body = await res.json().catch(() => null) as { status?: string } | null;
  return body?.status === 'ok' ? 'ok' : 'unknown';
}
